import { FC, useEffect, useState } from 'react';
import { Alert, Autocomplete, Button, Checkbox, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Grid, MenuItem, TextField } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import { toast } from 'react-toastify';
import { errorMessage, formatMoney, platformApi } from './platformApi';

// seat prices come from the account's plan (Plans → Edit), for its current period
const seatPriceText = (pricing: any, cycle: string | undefined, unit: 'user' | 'location') => {
  if (cycle !== 'monthly' && cycle !== 'yearly') return 'Priced in the contract';
  const price = pricing?.[`${unit}${cycle === 'monthly' ? 'Monthly' : 'Yearly'}`];
  return price ? `${formatMoney(price)} per ${unit} for this ${cycle} plan` : `Not priced for ${cycle} plans`;
};

type Product = 'erp' | 'pos';

interface IActionProps {
  accountId: string;
  product: Product;
  onClose: () => void;
  // called after a successful change so the page can reload
  onDone: () => void;
}

interface IShellProps {
  title: string;
  submitLabel: string;
  onClose: () => void;
  onDone: () => void;
  // returns the request to run; every action carries a reason
  submit: (reason: string) => Promise<any>;
  disabled?: boolean;
  danger?: boolean;
}

const ActionShell: FC<IShellProps> = ({ title, submitLabel, onClose, onDone, submit, disabled, danger, children }) => {
  const [reason, setReason] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const onSubmit = async () => {
    setError('');
    setLoading(true);
    try {
      await submit(reason.trim());
      onDone();
      onClose();
    } catch (err: any) {
      setError(errorMessage(err));
      setLoading(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ bgcolor: '#e9ecf1', py: 1.5, borderBottom: '1px solid #dcdcdc' }}>{title}</DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
        <Grid container spacing={2}>
          {children}
          <Grid item xs={12}>
            <TextField label='Reason' size='small' fullWidth required multiline minRows={2} value={reason} onChange={(e) => setReason(e.target.value)} helperText='Saved in the subscription history' />
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size='small' variant='outlined' onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <LoadingButton size='small' variant='contained' color={danger ? 'error' : 'primary'} loading={loading} disabled={disabled || !reason.trim()} onClick={onSubmit} sx={{ textTransform: 'none' }}>{submitLabel}</LoadingButton>
      </DialogActions>
    </Dialog>
  );
};

interface IExtendProps extends IActionProps {
  // the subscription has ended: reopen it on the same plan
  reopen?: boolean;
}

export const ExtendDialog: FC<IExtendProps> = ({ accountId, product, reopen, onClose, onDone }) => {
  const [days, setDays] = useState('30');
  const [until, setUntil] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [comp, setComp] = useState(false);
  // reopening needs a payment or the complimentary tick; an extension can be goodwill
  const paymentMissing = reopen && !comp && !(Number(amount) > 0 && paymentMode);

  return (
    <ActionShell
      title={`${reopen ? 'Reopen' : 'Extend'} ${product.toUpperCase()} subscription`}
      submitLabel={reopen ? 'Reopen' : 'Extend'}
      onClose={onClose}
      onDone={onDone}
      disabled={(!until && !(Number(days) > 0)) || paymentMissing || (Number(amount) > 0 && !paymentMode && !comp)}
      submit={(reason) => platformApi.post(`/accounts/${accountId}/extend`, { product, reason, amount, paymentRef, paymentMode, source: comp ? 'comp' : 'manual', ...(until ? { until } : { days: Number(days) }) })}
    >
      {reopen && (
        <Grid item xs={12}>
          <Alert severity='info'>Makes the same plan active again from today. Enter days or pick an end date, then record the payment or mark it complimentary.</Alert>
        </Grid>
      )}
      <Grid item xs={6}>
        <TextField label={reopen ? 'Days from today' : 'Days to add'} type='number' size='small' fullWidth value={days} disabled={!!until} onChange={(e) => setDays(e.target.value)} />
      </Grid>
      <Grid item xs={6}>
        <TextField label='Or set end date' type='date' size='small' fullWidth InputLabelProps={{ shrink: true }} value={until} onChange={(e) => setUntil(e.target.value)} />
      </Grid>
      <Grid item xs={4}>
        <TextField label='Amount received (₹)' type='number' size='small' fullWidth required={!!reopen && !comp} disabled={comp} value={amount} onChange={(e) => setAmount(e.target.value)} />
      </Grid>
      <Grid item xs={4}>
        <TextField select label='Payment mode' size='small' fullWidth required={!!reopen && !comp} disabled={comp} value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
          {['UPI', 'Bank transfer', 'Cheque', 'Cash', 'Card'].map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={4}>
        <TextField label='Payment reference' size='small' fullWidth disabled={comp} value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel control={<Checkbox size='small' checked={comp} onChange={(e) => setComp(e.target.checked)} />} label='Complimentary (partner or pilot, no payment)' />
      </Grid>
    </ActionShell>
  );
};

export const EndDialog: FC<IActionProps> = ({ accountId, product, onClose, onDone }) => (
  <ActionShell
    title={`End ${product.toUpperCase()} subscription now`}
    submitLabel='End now'
    danger
    onClose={onClose}
    onDone={onDone}
    submit={(reason) => platformApi.post(`/accounts/${accountId}/end`, { product, reason })}
  >
    <Grid item xs={12}>
      <Alert severity='warning'>
        {product === 'erp'
          ? 'The subscription ends immediately, with no grace period. The customer can still sign in, but every page shows a "renew your subscription" screen until it is renewed. No data is deleted.'
          : 'POS is switched off immediately (posEnabled off), with no grace period. The customer sees a renew-POS message in the ERP; the ERP keeps working. No data is deleted.'}
        {' '}The owner is notified on in-app, email, SMS and WhatsApp. Use Reopen to restore it after payment.
      </Alert>
    </Grid>
  </ActionShell>
);

interface IActivateProps extends IActionProps {
  currentPlan?: string;
  locations: { _id: string; name: string; code: string; accountName?: string }[];
  licensed: string[];
}

// outlets can be in any of the owner's companies
const outletLabel = (l: { name: string; code: string; accountName?: string }) => `${l.accountName ? `${l.accountName} · ` : ''}${l.name} (${l.code})`;

export const ActivateDialog: FC<IActivateProps> = ({ accountId, product, currentPlan, locations, licensed, onClose, onDone }) => {
  const [plans, setPlans] = useState<any[]>([]);
  const [planCode, setPlanCode] = useState(currentPlan || '');
  const [billingCycle, setBillingCycle] = useState('yearly');
  const [endDate, setEndDate] = useState('');
  const [amount, setAmount] = useState('');
  const [paymentRef, setPaymentRef] = useState('');
  const [paymentMode, setPaymentMode] = useState('');
  const [comp, setComp] = useState(false);
  const [posLocations, setPosLocations] = useState<string[]>(licensed);

  useEffect(() => {
    let active = true;
    platformApi.get('/plans').then((response) => {
      if (active) setPlans(response.data.plans.filter((p: any) => p.product === product && !p.isTemplate && p.status === 'active' && (!p.forAccount || p.forAccount._id === accountId)));
    }).catch(() => undefined);
    return () => { active = false; };
  }, [product, accountId]);

  const plan = plans.find((p) => p.code === planCode);
  const listPrice = plan?.pricing ? plan.pricing[billingCycle === 'monthly' ? 'monthly' : 'yearly'] : undefined;
  const custom = billingCycle === 'custom';

  return (
    <ActionShell
      title={`Activate ${product.toUpperCase()} plan`}
      submitLabel='Activate'
      onClose={onClose}
      onDone={onDone}
      disabled={!planCode || (custom && !endDate) || (product === 'pos' && !posLocations.length) || (!comp && !(Number(amount) > 0 && paymentMode))}
      submit={(reason) => platformApi.post(`/accounts/${accountId}/activate`, {
        product, planCode, reason, paymentRef, paymentMode, amount,
        billingCycle: custom ? 'yearly' : billingCycle,
        endDate: custom ? endDate : undefined,
        source: comp ? 'comp' : 'manual',
        posLocations: product === 'pos' ? posLocations : undefined,
      })}
    >
      <Grid item xs={12}>
        <Alert severity='info'>This starts a new period from today on the selected plan and ends any legacy full access.</Alert>
      </Grid>
      <Grid item xs={6}>
        <TextField select label='Plan' size='small' fullWidth required value={planCode} onChange={(e) => setPlanCode(e.target.value)}>
          {plans.map((p) => <MenuItem key={p.code} value={p.code}>{p.name}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={6}>
        <TextField select label='Period' size='small' fullWidth value={billingCycle} onChange={(e) => setBillingCycle(e.target.value)} helperText={listPrice !== undefined ? `List price ${formatMoney(listPrice)} + GST${product === 'pos' ? ' per outlet' : ''}` : ' '}>
          {product === 'erp' && <MenuItem value='monthly'>1 month</MenuItem>}
          <MenuItem value='yearly'>1 year</MenuItem>
          <MenuItem value='custom'>Custom end date</MenuItem>
        </TextField>
      </Grid>
      {custom && (
        <Grid item xs={6}>
          <TextField label='End date' type='date' size='small' fullWidth required InputLabelProps={{ shrink: true }} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
        </Grid>
      )}
      {product === 'pos' && (
        <Grid item xs={12}>
          <TextField select label='Licensed outlets' size='small' fullWidth required SelectProps={{ multiple: true, renderValue: (v) => locations.filter((l) => (v as string[]).includes(l._id)).map(outletLabel).join(', ') }} value={posLocations} onChange={(e) => setPosLocations(e.target.value as unknown as string[])} helperText='One licence per billing location'>
            {locations.map((l) => <MenuItem key={l._id} value={l._id}><Checkbox size='small' checked={posLocations.includes(l._id)} />{outletLabel(l)}</MenuItem>)}
          </TextField>
        </Grid>
      )}
      <Grid item xs={4}>
        <TextField label='Amount received (₹)' type='number' size='small' fullWidth required={!comp} disabled={comp} value={amount} onChange={(e) => setAmount(e.target.value)} />
      </Grid>
      <Grid item xs={4}>
        <TextField select label='Payment mode' size='small' fullWidth required={!comp} disabled={comp} value={paymentMode} onChange={(e) => setPaymentMode(e.target.value)}>
          {['UPI', 'Bank transfer', 'Cheque', 'Cash', 'Card'].map((m) => <MenuItem key={m} value={m}>{m}</MenuItem>)}
        </TextField>
      </Grid>
      <Grid item xs={4}>
        <TextField label='Payment reference' size='small' fullWidth disabled={comp} value={paymentRef} onChange={(e) => setPaymentRef(e.target.value)} helperText='UPI / bank / cheque ref' />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel control={<Checkbox size='small' checked={comp} onChange={(e) => setComp(e.target.checked)} />} label='Complimentary (partner or pilot, no payment)' />
      </Grid>
    </ActionShell>
  );
};

interface ISeatsProps extends IActionProps {
  users: number;
  locations: number;
  seatPricing?: Record<string, number | undefined>;
  billingCycle?: string;
}

export const SeatsDialog: FC<ISeatsProps> = ({ accountId, users, locations, seatPricing, billingCycle, onClose, onDone }) => {
  const [extraUsers, setExtraUsers] = useState(String(users));
  const [extraLocations, setExtraLocations] = useState(String(locations));

  return (
    <ActionShell
      title='Extra seats'
      submitLabel='Save'
      onClose={onClose}
      onDone={onDone}
      submit={(reason) => platformApi.put(`/accounts/${accountId}/seats`, { reason, users: Number(extraUsers) || 0, locations: Number(extraLocations) || 0 })}
    >
      <Grid item xs={12}>
        <Alert severity='info'>Total extra seats on this account. They are added to the plan's limits and stay valid until the current plan period ends.</Alert>
      </Grid>
      <Grid item xs={6}>
        <TextField label='Extra users' type='number' size='small' fullWidth value={extraUsers} onChange={(e) => setExtraUsers(e.target.value)} helperText={seatPriceText(seatPricing, billingCycle, 'user')} />
      </Grid>
      <Grid item xs={6}>
        <TextField label='Extra locations' type='number' size='small' fullWidth value={extraLocations} onChange={(e) => setExtraLocations(e.target.value)} helperText={seatPriceText(seatPricing, billingCycle, 'location')} />
      </Grid>
    </ActionShell>
  );
};

interface ILinkProps extends IActionProps {
  linkable: { _id: string; name: string; tinGst?: string }[];
}

export const LinkCompanyDialog: FC<ILinkProps> = ({ accountId, linkable, onClose, onDone }) => {
  const [linkAccountId, setLinkAccountId] = useState('');
  return (
    <ActionShell
      title='Link company'
      submitLabel='Link'
      onClose={onClose}
      onDone={onDone}
      disabled={!linkAccountId}
      submit={(reason) => platformApi.post(`/accounts/${accountId}/link-account`, { linkAccountId, reason })}
    >
      <Grid item xs={12}>
        <Alert severity='info'>The company shares this subscription (plan, end date and modules). Its own subscription, if any, is replaced. Only the owner's companies can be linked.</Alert>
      </Grid>
      <Grid item xs={12}>
        <TextField select label='Company' size='small' fullWidth required value={linkAccountId} onChange={(e) => setLinkAccountId(e.target.value)} helperText={linkable.length ? ' ' : 'The owner has no other companies'}>
          {linkable.map((a) => <MenuItem key={a._id} value={a._id}>{a.name}{a.tinGst ? ` (${a.tinGst})` : ''}</MenuItem>)}
        </TextField>
      </Grid>
    </ActionShell>
  );
};

export const UnlinkCompanyDialog: FC<IActionProps & { company: { _id: string; name: string } }> = ({ accountId, company, onClose, onDone }) => (
  <ActionShell
    title={`Remove ${company.name}`}
    submitLabel='Remove'
    danger
    onClose={onClose}
    onDone={onDone}
    submit={(reason) => platformApi.post(`/accounts/${accountId}/unlink-account`, { linkAccountId: company._id, reason })}
  >
    <Grid item xs={12}>
      <Alert severity='warning'>{company.name} will no longer be covered by this subscription and will need its own plan.</Alert>
    </Grid>
  </ActionShell>
);

const NOTICE_OPTIONS = [
  { value: 'expiry_warning', label: 'Expiry warning', template: 'subscription_expiry_warning' },
  { value: 'overdue_suspension', label: 'Payment overdue · suspension date', template: 'subscription_overdue_suspension' },
  { value: 'ended', label: 'Subscription expired', template: 'subscription_ended' },
];
const CHANNEL_LABEL: Record<string, string> = { app: 'In-app', email: 'Email', sms: 'SMS', whatsapp: 'WhatsApp' };
const CHANNEL_OPTIONS = [
  { value: 'app', label: 'In-app (ERP bell)' },
  { value: 'email', label: 'Email' },
  { value: 'sms', label: 'SMS' },
  { value: 'whatsapp', label: 'WhatsApp' },
];

interface INoticeProps extends Omit<IActionProps, 'accountId'> {
  // without an account the form starts with a business picker (Notifications page)
  accountId?: string;
}

export const NoticeDialog: FC<INoticeProps> = ({ accountId: fixedAccountId, product: initialProduct, onClose, onDone }) => {
  const [account, setAccount] = useState<{ _id: string; name: string } | null>(null);
  const [accountOptions, setAccountOptions] = useState<{ _id: string; name: string }[]>([]);
  const [search, setSearch] = useState('');
  const [product, setProduct] = useState<Product>(initialProduct);
  const accountId = fixedAccountId || account?._id;
  const [kind, setKind] = useState('expiry_warning');
  const [channels, setChannels] = useState<string[]>(CHANNEL_OPTIONS.map((c) => c.value));
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);

  const toggle = (value: string, on: boolean) => setChannels(on ? [...channels, value] : channels.filter((c) => c !== value));

  useEffect(() => {
    if (fixedAccountId) return undefined;
    const timer = setTimeout(() => {
      platformApi.get(`/accounts?limit=20${search ? `&search=${encodeURIComponent(search)}` : ''}`)
        .then((response) => setAccountOptions(response.data.accounts))
        .catch(() => undefined);
    }, 300);
    return () => clearTimeout(timer);
  }, [search, fixedAccountId]);

  const send = async () => {
    setError('');
    setSending(true);
    try {
      const response = await platformApi.post(`/accounts/${accountId}/notify`, { kind, product, channels });
      const { sent, skipped } = response.data;
      // success closes the form; an error keeps it open with the message
      toast.success(`Notice sent: ${sent.map((c: string) => CHANNEL_LABEL[c] || c).join(', ') || 'none'}${skipped.length ? ` (skipped, no email/phone: ${skipped.map((c: string) => CHANNEL_LABEL[c] || c).join(', ')})` : ''}`);
      onDone();
      onClose();
    } catch (err: any) {
      setError(errorMessage(err));
      setSending(false);
    }
  };

  return (
    <Dialog open onClose={onClose} maxWidth='sm' fullWidth>
      <DialogTitle sx={{ bgcolor: '#e9ecf1', py: 1.5, borderBottom: '1px solid #dcdcdc' }}>{fixedAccountId ? `Send ${product.toUpperCase()} subscription notice` : 'Send notice'}</DialogTitle>
      <DialogContent sx={{ pt: '20px !important' }}>
        {error && <Alert severity='error' sx={{ mb: 2 }}>{error}</Alert>}
        {!fixedAccountId && (
          <Grid container spacing={2} sx={{ mb: 2 }}>
            <Grid item xs={8}>
              <Autocomplete
                options={accountOptions}
                value={account}
                getOptionLabel={(o) => o.name}
                isOptionEqualToValue={(a, b) => a._id === b._id}
                onChange={(e, value) => setAccount(value)}
                onInputChange={(e, value) => setSearch(value)}
                renderInput={(params) => <TextField {...params} label='Business' size='small' required />}
              />
            </Grid>
            <Grid item xs={4}>
              <TextField select label='Subscription' size='small' fullWidth value={product} onChange={(e) => setProduct(e.target.value as Product)}>
                <MenuItem value='erp'>ERP</MenuItem>
                <MenuItem value='pos'>POS</MenuItem>
              </TextField>
            </Grid>
          </Grid>
        )}
        <TextField select label='Notice' size='small' fullWidth value={kind} onChange={(e) => setKind(e.target.value)} sx={{ mb: 2 }}
          helperText={`WhatsApp template: ${NOTICE_OPTIONS.find((o) => o.value === kind)?.template}`}>
          {NOTICE_OPTIONS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <Grid container>
          {CHANNEL_OPTIONS.map((c) => (
            <Grid item xs={6} key={c.value}>
              <FormControlLabel control={<Checkbox size='small' checked={channels.includes(c.value)} onChange={(e) => toggle(c.value, e.target.checked)} />} label={c.label} />
            </Grid>
          ))}
        </Grid>
        <h5 style={{ fontWeight: 'normal', color: '#6b7280', fontSize: 12, margin: '8px 0 0' }}>Sent to the account owner's email and phone. Recorded in the subscription history.</h5>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button size='small' variant='outlined' onClick={onClose} sx={{ textTransform: 'none' }}>Cancel</Button>
        <LoadingButton size='small' variant='contained' loading={sending} disabled={!channels.length || !accountId} onClick={send} sx={{ textTransform: 'none' }}>Send</LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
